import { DataProxy } from 'apollo-cache'
import { useLocation, matchPath } from 'react-router-dom'
import produce from 'immer'

import {
  useCommentCreatedSubscription,
  CommentCreatedSubscriptionResult,
  CommentInfoFragment,
  CommentInfoFragmentDoc,
} from 'gqlTypes'
import { dataIdFromObject } from 'graphql/apolloClient'
import { routes } from 'constants/routes'
import { useThreadRouteParams } from 'utils/hooks/useThreadRouteParams'
import { ErrorHandler } from 'modules/tracing'
import { updateActivityFeed } from './updateActivityFeed'
import { useMarkCommentsAsRead } from './useMarkCommentsAsRead'

interface CommentCreatedProps {
  shareIdentifier: string
  isAuthenticated: boolean
}

// Subscription to:
// - show new comments or replies
// - mark comments/ replies as read
export const useCommentCreatedSubscriptionWrapper = ({
  shareIdentifier,
  isAuthenticated,
}: CommentCreatedProps) => {
  const { pathname } = useLocation()
  const { threadIdentifier } = useThreadRouteParams()

  const markCommentsAsRead = useMarkCommentsAsRead()

  const getParentComment = (
    client: DataProxy,
    id: string
  ): CommentInfoFragment => {
    return client.readFragment({
      fragment: CommentInfoFragmentDoc,
      fragmentName: 'CommentInfo',
      id,
    }) as CommentInfoFragment
  }

  const updateActivitiesCacheForNewComment = (
    client: DataProxy,
    { data }: CommentCreatedSubscriptionResult
  ) => {
    if (!data || data.commentCreated.userIsCreator) return

    const permanentArtboardShortId = data.commentCreated.artboard
      ?.permanentArtboardShortId!

    try {
      if (data.commentCreated.parent) {
        const fragmentId = dataIdFromObject({
          __typename: 'Comment',
          identifier: data.commentCreated.parent.identifier,
        }) as string
        const parentComment = getParentComment(client, fragmentId)

        // NOTE: if CommentInfoFragment is not in cache, it's fine because we
        // only want to update comments in cache
        if (parentComment) {
          const newComment = produce(parentComment, draft => {
            if (!data.commentCreated.parent) {
              ErrorHandler.shouldNeverHappen(
                'There should be always a parent comment when creating a reply'
              )
              return
            }

            draft.subscriptionStatus =
              data.commentCreated.parent.subscriptionStatus
            draft?.replies?.unshift(data.commentCreated)
          })

          client.writeFragment<CommentInfoFragment>({
            fragment: CommentInfoFragmentDoc,
            fragmentName: 'CommentInfo',
            id: fragmentId,
            data: newComment,
          })
        }
      } else {
        updateActivityFeed({
          client,
          variables: { permanentArtboardShortId, shareIdentifier },
          update: activityFeed => {
            activityFeed.entries.unshift(data.commentCreated)
          },
        })
      }
    } catch (error) {
      // NOTE: throws an error in Artboards list because GetActivitiesDocument is not in cache,
      // it's fine because we only want to update GetActivitiesDocument from Artboard detail page
    }
  }

  /**
   * Comments should only be marked as read in the following scenarios:
   * - when viewing artboard details, the new comment corresponds to this
   * artboard and is not a reply comment
   * - when viewing the thread panel, the new comment is a reply comment and
   * corresponds to that thread
   */
  const markNewCommentsOrRepliesAsRead = ({
    data,
  }: CommentCreatedSubscriptionResult) => {
    const artboardRoute = matchPath(pathname, {
      path: routes.ARTBOARD_DETAIL.template(),
      exact: true,
    }) as {
      params?: { permanentArtboardShortId?: string }
      isExact?: boolean
    }

    const permanentArtboardShortId = data?.commentCreated.artboard
      ?.permanentArtboardShortId!
    const parentCommentIdentifier = data?.commentCreated.parent?.identifier

    const isCommentFromCurrentArtboard =
      artboardRoute?.params?.permanentArtboardShortId ===
      permanentArtboardShortId
    const isActivitiesPanel = artboardRoute?.isExact && !threadIdentifier
    const isThreadPanel = threadIdentifier === parentCommentIdentifier

    if (
      isCommentFromCurrentArtboard &&
      ((isActivitiesPanel && !parentCommentIdentifier) || isThreadPanel)
    ) {
      markCommentsAsRead({
        permanentArtboardShortId: permanentArtboardShortId,
        identifiers: [data?.commentCreated?.identifier!],
      })
    }
  }

  useCommentCreatedSubscription({
    variables: { shareIdentifier },
    onSubscriptionData: ({ client, subscriptionData }) => {
      updateActivitiesCacheForNewComment(client, subscriptionData)

      markNewCommentsOrRepliesAsRead(subscriptionData)
    },
    skip: !isAuthenticated,
  })
}
